import { baseApi, TAG } from '../../app/api/baseApi'

const usersTag = (clientId) => ({ type: TAG.CLIENT_USER, id: clientId })
const byId = (left, right) => left.id - right.id

export const clientUsersApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        // The caller's clients with their role and permissions in each. `permissions` is null when
        // they could not be loaded. Ordered by id, so the first is the client the API works in by default.
        getMyClientAccess: builder.query({
            async queryFn(arg, api, extraOptions, fetchWithBaseQuery) {
                const clients = await fetchWithBaseQuery('/Client/clients')
                if (clients.error) {
                    return { error: clients.error }
                }
                const list = Array.isArray(clients.data) ? [...clients.data].sort(byId) : []
                const data = await Promise.all(list.map(async (client) => {
                    const response = await fetchWithBaseQuery(`/ClientUser/${client.id}/MyPermissions`)
                    return {
                        id: client.id,
                        name: client.name,
                        role: response.data ? response.data.role : null,
                        permissions: response.data ? response.data.permissions : null
                    }
                }))
                return { data }
            },
            providesTags: [TAG.CLIENT_PERMISSION]
        }),
        getMySubscriptionPermissions: builder.query({
            query: (subscriptionId) => `/ClientUser/subscription/${subscriptionId}/MyPermissions`,
            providesTags: [TAG.CLIENT_PERMISSION]
        }),
        getRoleCatalog: builder.query({
            query: () => '/ClientUser/Roles'
        }),
        getClientUsers: builder.query({
            query: (clientId) => `/ClientUser/${clientId}/Users`,
            transformResponse: (data) => (Array.isArray(data) ? data : []),
            providesTags: (result, error, clientId) => [usersTag(clientId)]
        }),
        inviteClientUser: builder.mutation({
            query: (body) => ({ url: '/ClientUser/Invite', method: 'POST', body }),
            invalidatesTags: (result, error, { clientId }) => (result && result.result ? [usersTag(clientId)] : [])
        }),
        changeClientUserStatus: builder.mutation({
            query: ({ id, isActive }) => ({ url: `/ClientUser/${id}/ChangeStatus/${isActive}`, method: 'POST', body: {} }),
            invalidatesTags: (result, error, { clientId }) => [usersTag(clientId)]
        }),
        // Emails the user a new temporary password. The caller never sees it.
        resetClientUserPassword: builder.mutation({
            query: ({ id }) => ({ url: `/ClientUser/${id}/ResetPassword`, method: 'POST', body: {} })
        }),
        // Also refreshes permissions, because the caller may have changed their own role.
        updateClientUserRole: builder.mutation({
            query: ({ id, role }) => ({ url: `/ClientUser/${id}/Role`, method: 'PUT', body: { role } }),
            invalidatesTags: (result, error, { clientId }) => [usersTag(clientId), TAG.CLIENT_PERMISSION]
        })
    })
})

export const {
    useGetMyClientAccessQuery,
    useGetMySubscriptionPermissionsQuery,
    useGetRoleCatalogQuery,
    useGetClientUsersQuery,
    useInviteClientUserMutation,
    useChangeClientUserStatusMutation,
    useResetClientUserPasswordMutation,
    useUpdateClientUserRoleMutation
} = clientUsersApi
